import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider, googleLogout } from '@react-oauth/google';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import ConnectionManager from './components/ConnectionManager';
import PipelineManager from './components/PipelineManager';
import PipelineConfiguration from './components/PipelineConfiguration';
import GA4SessionizationForm from './components/forms/GA4SessionizationForm';
import GA4FlattenerForm from './components/forms/GA4FlattenerForm';
import MarketingAttributionForm from './components/forms/MarketingAttributionForm';
import GoogleAdsCostLoaderForm from './components/forms/GoogleAdsCostLoaderForm';
import ExecutionManager from './components/ExecutionManager';
import AuthGate from './components/AuthGate';
import './App.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Debug: log client ID (remove in production)
if (!GOOGLE_CLIENT_ID) {
  console.warn('[App] REACT_APP_GOOGLE_CLIENT_ID is not set!');
} else {
  console.log('[App] Google Client ID loaded:', GOOGLE_CLIENT_ID.substring(0, 20) + '...');
}

function NavBar({ activeTab }: { activeTab: string }) {
  return (
    <div className="tab-buttons">
      <Link to="/connections">
        <button className={activeTab === 'connections' ? 'active' : ''}>
          Connections
        </button>
      </Link>
      <Link to="/pipelines">
        <button className={activeTab === 'pipelines' ? 'active' : ''}>
          Pipelines
        </button>
      </Link>
      <Link to="/executions">
        <button className={activeTab === 'executions' ? 'active' : ''}>
          Executions
        </button>
      </Link>
    </div>
  );
}

function AppContent() {
  const [userInfo, setUserInfo] = useState<{ name?: string; email?: string } | null>(null);
  const location = useLocation();

  const handleLogout = () => {
    googleLogout();
    localStorage.removeItem('id_token');
    setUserInfo(null);
    window.location.reload();
  };

  const getActiveTab = (path: string) => {
    if (path.startsWith('/connections')) return 'connections';
    if (path.startsWith('/pipelines')) return 'pipelines';
    if (path.startsWith('/executions')) return 'executions';
    return 'connections';
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Marketing Attribution System</h1>
        {userInfo && (
          <div className="user-info">
            <span className="user-name">{userInfo.name}</span>
            <span className="user-email">{userInfo.email}</span>
            <button className="logout-button" onClick={handleLogout}>Logout</button>
          </div>
        )}
      </header>
      <main>
        <AuthGate onUserInfo={setUserInfo} onLogout={handleLogout}>
          <div className="app-tabs">
            <NavBar activeTab={getActiveTab(location.pathname)} />
            <div className="tab-content">
              <Routes>
                <Route path="/connections" element={<ConnectionManager />} />
                <Route path="/pipelines" element={<PipelineManager />} />
                <Route path="/pipelines/new/ga4-sessionization" element={<GA4SessionizationForm />} />
                <Route path="/pipelines/new/ga4-flattener" element={<GA4FlattenerForm />} />
                <Route path="/pipelines/new/marketing-attribution" element={<MarketingAttributionForm />} />
                <Route path="/pipelines/new/google-ads-cost-loader" element={<GoogleAdsCostLoaderForm />} />
                <Route path="/pipelines/:id/edit" element={<PipelineConfiguration />} />
                <Route path="/executions" element={<ExecutionManager />} />
                <Route path="/" element={<Navigate to="/connections" replace />} />
              </Routes>
            </div>
          </div>
        </AuthGate>
      </main>
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <AppContent />
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
